import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api-client";
import { useAgentJob } from "@/hooks/use-agent-jobs";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";

interface RegenerateAllDialogProps {
  brandId: string;
  projectId: string;
  /** DRAFT + PENDING_REVIEW + REJECTED count — these get discarded. */
  discardCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Regenerate all" confirm: discards the project's non-approved pieces
 * (DRAFT/PENDING_REVIEW/REJECTED — APPROVED/SCHEDULED/PUBLISHED/FAILED are
 * never touched) and asks the worker for a fresh set from the brief, then
 * polls the AgentJob until it settles.
 */
export function RegenerateAllDialog({
  brandId,
  projectId,
  discardCount,
  open,
  onOpenChange,
}: RegenerateAllDialogProps) {
  const { t } = useTranslation("projects");
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);

  const startMut = useMutation({
    mutationFn: () =>
      apiFetch<{ jobId: string; status: string }>(
        `/brands/${brandId}/projects/${projectId}/generate/copy`,
        { method: "POST", body: JSON.stringify({ replaceNonApproved: true }) },
      ),
    onSuccess: (data) => setJobId(data.jobId),
    onError: () => toast.error(t("projects:regenerateDialog.toast.startFailed")),
  });

  const { data: job } = useAgentJob(brandId, jobId);

  // Settle once per job: invalidate + toast + close.
  const settledJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (!job || !jobId || settledJobRef.current === jobId) return;
    if (job.status === "COMPLETED" || job.status === "FAILED") {
      settledJobRef.current = jobId;
      qc.invalidateQueries({ queryKey: ["project", brandId, projectId] });
      qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      if (job.status === "COMPLETED") {
        toast.success(t("projects:regenerateDialog.toast.success"));
      } else {
        toast.error(job.error ?? t("projects:regenerateDialog.toast.failed"));
      }
      setJobId(null);
      onOpenChange(false);
    }
  }, [job, jobId, brandId, projectId, qc, onOpenChange, t]);

  const busy = startMut.isPending || !!jobId;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("projects:regenerateDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("projects:regenerateDialog.description", { count: discardCount })}
          </DialogDescription>
        </DialogHeader>

        {busy && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            {jobId ? t("projects:regenerateDialog.generatingNew") : t("projects:regenerateDialog.starting")}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={() => startMut.mutate()} disabled={busy}>
            <RotateCcw size={14} className="mr-2" />
            {busy ? t("projects:regenerateDialog.regenerating") : t("projects:regenerateDialog.discardAndRegenerate", { count: discardCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
